import { FormattedMessage, useIntl } from 'react-intl';
import { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useSignIn, useIsAuthenticated } from 'react-auth-kit';
import { Link, Redirect } from 'react-router-dom';
import { Button, Checkbox, Input, Logo } from 'src/components';
import { useObtainTokenMutation, getAccountInfoFromJWT } from 'src/api/mutations/accessMutations';
import { useAlerts } from 'src/common/AlertManager';
import { AuthTokenResponse } from 'src/types/TApiQueries';
import styles from './styles.module.scss';
import messages from './messages';
import { useAtomValue } from 'jotai/utils';
import { securitySettingsAtom } from 'src/shared/settings/security';
import { config } from 'src/utils/config';
import SsoButtons from 'src/components/SsoButtons/SsoButtons';
import { withUtm } from 'src/utils/campaign';
import { sanitizeNext } from 'src/utils/nextPath';

export const Login = () => {
  const { formatMessage } = useIntl();
  const signIn = useSignIn();
  const { showAlert } = useAlerts();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checked, setChecked] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [ssoAvailable, setSsoAvailable] = useState(false);

  const obtainTokenMutation = useObtainTokenMutation({
    onSuccess: (result) => {
      setTokenOrThrow(result);
    },
  });

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    try {
      await obtainTokenMutation.mutateAsync({
        username: formData.email,
        password: formData.password,
        remember_me: checked,
      });
    } catch (error) {
      // Generic on purpose (no account enumeration). The second sentence
      // only appears when the stack offers social login: an account created
      // with Google / Microsoft has no password until its owner sets one.
      showAlert(
        ssoAvailable
          ? formatMessage(messages.loginFailedWithSso)
          : formatMessage(messages.loginFailed),
      );
    }
  };

  const setTokenOrThrow = ({
    jwt,
    accessID,
    notifications,
    permissions,
    preferences,
    role_id,
    role_name,
    uploadkey,
    username,
    user_email,
    user_id,
  }: AuthTokenResponse) => {
    if (
      signIn({
        token: jwt,
        expiresIn: checked ? 43200 : 1440,
        tokenType: 'Bearer',
        authState: {
          isLoggedIn: true,
          accessId: accessID,
          uploadkey,
          role: role_name,
          roleId: role_id,
          permissions,
          notifications,
          preferences,
          username,
          userEmail: user_email,
          userId: user_id,
        },
      })
    ) {
      setIsLoggedIn(true);
    } else {
      showAlert('Error while obtaining tokens, please try again');
    }
  };

  // a signed-in-only page (e.g. /upload/:linkId) sends ?next= to come back to
  const queryParams = new URLSearchParams(window.location.search);
  const next = sanitizeNext(queryParams.get('next'), '/');
  if (isLoggedIn) {
    return <Redirect to={next} />;
  }
  const jwt_token = queryParams.get('_jwt');
  if (jwt_token) {
    getAccountInfoFromJWT(jwt_token, (data) => {
      const AccountInfo = data.data;
    if (
      signIn({
        token: jwt_token,
        expiresIn: 1440,
        tokenType: 'Bearer',
          authState: {
            isLoggedIn: true,
            accessId: AccountInfo.access_id,
            role: AccountInfo.role_name,
            uploadKey: AccountInfo.uploadKey,
            roleId: AccountInfo.role_id,
            permissions: AccountInfo.permissions,
            username: AccountInfo.first_name,
            userEmail: AccountInfo.email,
            userId: AccountInfo.id,
          },
        })
      ) {
        setIsLoggedIn(true);
      }
    });
  }
  return (
    <div className={styles.formWrapper}>
      <form className={styles.form} onSubmit={onSubmit} data-test="login-form">
        <div className={styles.logoContainer}>
          <Logo linkable={false} />
        </div>
        <Input
          label={formatMessage(messages.email)}
          placeholder=""
          onChange={(newVal) => setFormData({ ...formData, email: newVal })}
        />
        <Input
          label={formatMessage(messages.password)}
          type="password"
          placeholder=""
          onChange={(newVal) => setFormData({ ...formData, password: newVal })}
        />
        <div className={styles.splitWrapper}>
          <Checkbox
            label={formatMessage(messages.rememberMe)}
            checked={checked}
            onChange={() => setChecked((e) => !e)}
          />
          <a href={`${config.hostUrl}/usersecurity/forgotpassword`}>
            <FormattedMessage {...messages.forgotPassword} />
          </a>
        </div>
        <div className={styles.buttonWrapper}>
          <Button type="submit" size="small" loading={obtainTokenMutation.isLoading}>
            <FormattedMessage {...messages.signIn} />
          </Button>
        </div>

        {/* Social login (sign-in only; accounts are created on /signup).
            Renders nothing on a stack with no providers configured. */}
        <SsoButtons returnTo={next === '/' ? '/assets' : next} intent="login" className={styles.sso} onAvailability={setSsoAvailable} />

        <p className={styles.createAccount}>
          <FormattedMessage {...messages.newTo} />{' '}
          <Link to={withUtm('/signup', window.location.search)}>
            <FormattedMessage {...messages.createAcc} />
          </Link>
        </p>

        <div className={styles.termsAndPolicyContainer}>
          <a href="https://filespin.io/terms">Terms of Service</a>
          <span>|</span>
          <a href="https://filespin.io/privacy/">Privacy Policy</a>
        </div>
      </form>
    </div>
  );
};
